<template>
	<v-card class="d-flex flex-column pb-5" :loading="loading">
		<v-toolbar color="primary" dark flat>
			<v-toolbar-title style="flex: 0 1 auto">
				Source Data Extraction
			</v-toolbar-title>

			<!-- One toolbar search whose target switches with the active tab:
			     on the Assigned Projects tab (0) it filters the projects list;
			     on a project tab it filters that project's papers. Cleared on
			     tab switch (see the currentTab watcher) so each mode starts
			     fresh. Left-aligned next to the title (ml-4), matching the other
			     table toolbars (PaperTable / QC dashboard / Completed Extractions). -->
			<v-text-field
				v-model="searchKey"
				prepend-inner-icon="mdi-magnify"
				:placeholder="
					currentTab === 0 ? 'Search projects...' : 'Search papers...'
				"
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				clearable
				bg-color="white"
				class="flex-grow-0 ml-4 sde-dash-search"
			/>

			<v-spacer />

			<template #extension>
				<v-tabs
					v-model="currentTab"
					bg-color="transparent"
					color="secondary"
					slider-size="3"
					show-arrows
					selected-class="tabs-slider"
				>
					<v-tab
						:value="0"
						class="font-weight-bold"
						prepend-icon="mdi-clipboard-list-outline"
					>
						Assigned Projects
					</v-tab>
					<v-tab
						v-for="(project, index) in projects"
						:key="project.uuid"
						:value="index + 1"
						class="font-weight-bold"
					>
						<!-- Outline variants: the tab strip sits on the primary (teal)
						toolbar extension. Urgent projects keep the alarm marker. -->
						<v-icon start size="small">
							{{ project.isUrgent ? "mdi-alarm" : "mdi-folder-outline" }}
						</v-icon>
						{{ project.name }}
					</v-tab>
				</v-tabs>
			</template>
		</v-toolbar>

		<v-tabs-window v-model="currentTab">
			<v-tabs-window-item :value="0">
				<my-projects-table
					:projects="tab0Rows"
					@open-project="openProject"
				/>
			</v-tabs-window-item>

			<v-tabs-window-item
				v-for="(project, index) in projects"
				:key="project.uuid"
				:value="index + 1"
			>
				<!-- Mount (and fetch) a project tab only while it is active, so
				     we do not fan out a my-detail request for every project on
				     load; returning to a tab re-fetches fresh lists. -->
				<project-tab
					v-if="currentTab === index + 1"
					:project-uuid="project.uuid"
					:project-name="project.name"
					:external-search="searchKey"
				/>
			</v-tabs-window-item>
		</v-tabs-window>
	</v-card>
</template>

<script>
import _ from "lodash";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import MyProjectsTable from "@/pages/SdeProjectDashboard/MyProjectsTable";
import ProjectTab from "@/pages/SdeProjectDashboard/ProjectTab";

export default {
	name: "SdeProjectDashboard",

	components: {
		MyProjectsTable,
		ProjectTab,
	},

	data() {
		return {
			currentTab: 0,
			loading: false,
			// Full member/org project set from the API: urgent first, then by
			// priority. Drives BOTH the tab strip and (when not searching) the
			// tab-0 table - so it is never narrowed by the search.
			projects: [],
			// DCTL-2156: tab-0 search results from /projects/mine?search=. Kept
			// separate from `projects` so searching narrows only the table, not
			// the per-project tab strip. Null = no active search (show `projects`).
			projectSearchResults: null,
			// Monotonic id to drop stale search responses (see fetchProjectSearch).
			searchSeq: 0,
			// Drives the single toolbar search; its target depends on currentTab
			// (projects list on tab 0, papers within the active project tab).
			searchKey: "",
		};
	},

	computed: {
		// The rows the tab-0 table shows: server search results when a term is
		// active, otherwise the full project list.
		tab0Rows() {
			return this.projectSearchResults ?? this.projects;
		},
	},

	watch: {
		// Reflect the active tab in ?project=<uuid> (the overview clears it) so the
		// URL is shareable and the browser Back button returns to the last-viewed
		// tab. replace(), not push(): a tab switch is not a navigation step.
		currentTab(val) {
			// Each tab is a distinct search mode (projects vs papers), so start
			// the new tab with an empty query rather than carrying the term over.
			this.searchKey = "";
			// Drop any in-flight / rendered tab-0 project search on tab switch.
			this.debouncedProjectSearch.cancel();
			this.projectSearchResults = null;
			const project = val > 0 ? this.projects[val - 1] : null;
			const nextUuid = project ? project.uuid : undefined;
			if ((this.$route.query.project || undefined) === nextUuid) return;
			const query = { ...this.$route.query };
			if (nextUuid) {
				query.project = nextUuid;
			} else {
				delete query.project;
			}
			this.$router.replace({ query }).catch(() => {
				// ignore the redundant-navigation error when the query is unchanged
			});
		},

		// DCTL-2156: on the projects tab, drive a debounced server search of
		// /projects/mine (name OR contained paper). Project tabs route the term
		// to their own paper search (ProjectTab), so this is tab-0 only.
		searchKey() {
			this.handleProjectSearchChange();
		},
	},

	created() {
		// One debounced instance per component, with a stable `this` (mirrors the
		// DCTL-2133 Available-search debounce in ProjectTab).
		this.debouncedProjectSearch = _.debounce(this.fetchProjectSearch, 300);
	},

	mounted() {
		this.getProjects();
	},

	beforeUnmount() {
		this.debouncedProjectSearch.cancel();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		getProjects() {
			this.loading = "secondary";
			this.$http
				.get("/projects/mine")
				.then((response) => {
					this.projects = response.data.projects;
					this.applyTabFromRoute();
				})
				.catch((error) => this.handleError(error))
				.finally(() => {
					this.loading = false;
				});
		},

		// DCTL-2156: react to a change in the toolbar term while on the projects
		// tab. Empty term (or a non-projects tab) clears the search and reverts
		// the table to the full project list; a term debounces a server search.
		handleProjectSearchChange() {
			const term = (this.searchKey || "").trim();
			if (this.currentTab !== 0 || !term) {
				this.debouncedProjectSearch.cancel();
				this.projectSearchResults = null;
				return;
			}
			this.debouncedProjectSearch(term);
		},

		// DCTL-2156: fetch member/org projects matching `term` by name OR by a
		// contained paper. Stale responses (a newer keystroke, a cleared search,
		// or a tab switch) are dropped via the monotonic searchSeq.
		fetchProjectSearch(term) {
			const seq = ++this.searchSeq;
			this.loading = "secondary";
			this.$http
				.get("/projects/mine", { params: { search: term } })
				.then((response) => {
					if (seq !== this.searchSeq) return;
					this.projectSearchResults = response.data.projects || [];
				})
				.catch((error) => {
					if (seq === this.searchSeq) this.handleError(error);
				})
				.finally(() => {
					if (seq === this.searchSeq) this.loading = false;
				});
		},

		// Restore the active project tab from ?project=<uuid> (deep links + the
		// Back button). Absent/unknown uuid falls back to the overview (tab 0).
		applyTabFromRoute() {
			const uuid = this.$route.query.project;
			if (!uuid) return;
			const index = this.projects.findIndex((p) => p.uuid === uuid);
			this.currentTab = index === -1 ? 0 : index + 1;
		},

		openProject(uuid) {
			const index = this.projects.findIndex((p) => p.uuid === uuid);
			if (index !== -1) this.currentTab = index + 1;
		},

		handleError(error) {
			if (error.response?.data?.message) {
				this.showNotification(error.response.data);
			} else if (error.response?.data?.error) {
				this.showNotification(error.response.data.error);
			}
		},
	},
};
</script>

<style scoped lang="scss">
.sde-dash-search {
	min-width: 220px;
	max-width: 320px;
}
</style>
